import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { seriesList } from '@/mocks/series';

interface NavItem {
  label: string;
  to: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: '首页', to: '/' },
  { label: '追更大厅', to: '/chasing' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [kw, setKw] = useState('');

  const isHomeTop = pathname === '/' && !scrolled;
  const transparent = isHomeTop && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  const linkCls = (item: NavItem) =>
    `relative whitespace-nowrap text-sm transition-colors duration-200 ${
      pathname === item.to
        ? transparent
          ? 'text-background-50 font-bold'
          : 'text-primary-600 font-bold'
        : transparent
          ? 'text-background-50/85 hover:text-background-50'
          : 'text-foreground-700 hover:text-primary-600'
    }`;

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = kw.trim();
    window.REACT_APP_NAVIGATE(`/chasing${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/90 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 whitespace-nowrap"
          aria-label="吃瓜每日 首页"
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 via-primary-500 to-accent-500 text-lg ${
              transparent ? '' : 'ring-2 ring-background-50'
            }`}
          >
            🍉
          </span>
          <span
            className={`font-heading text-xl md:text-2xl tracking-wide ${
              transparent ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            吃瓜每日
          </span>
        </Link>

        <nav
          aria-label="主导航"
          className="hidden items-center gap-1 rounded-full border border-background-200/60 bg-background-50/60 px-1 py-1 md:flex"
        >
          {NAV_ITEMS.map((it) => (
            <Link key={it.to} to={it.to} className={`rounded-full px-5 py-2 ${linkCls(it)}`}>
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <form onSubmit={doSearch} className="relative">
            <i
              className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                transparent ? 'text-background-50/70' : 'text-foreground-400'
              }`}
            ></i>
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜一个瓜…"
              aria-label="搜索系列"
              className={`h-9 w-44 rounded-full pl-9 pr-3 text-sm outline-none transition-all duration-200 focus:w-52 ${
                transparent
                  ? 'bg-background-50/15 text-background-50 placeholder:text-background-50/60 focus:bg-background-50/25'
                  : 'border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:border-primary-300'
              }`}
            />
          </form>
          <Link
            to="/chasing"
            className="flex h-9 items-center whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            立即追更
            <i className="ri-arrow-right-up-line ml-1 text-base"></i>
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? '关闭菜单' : '打开菜单'}
          aria-expanded={open}
          className={`flex h-10 w-10 items-center justify-center rounded-full text-xl md:hidden ${
            transparent ? 'text-background-50' : 'text-foreground-950'
          }`}
        >
          <i className={open ? 'ri-close-line' : 'ri-menu-3-line'}></i>
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50/95 px-4 pb-6 pt-2 backdrop-blur">
          <nav aria-label="移动端导航" className="flex flex-col gap-1">
            {NAV_ITEMS.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                className={`rounded-xl px-4 py-3 text-base ${
                  pathname === it.to
                    ? 'bg-primary-100 font-bold text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {it.label}
              </Link>
            ))}
            <Link
              to="/chasing"
              className="mt-2 flex items-center justify-center whitespace-nowrap rounded-xl bg-primary-500 px-4 py-3 text-base font-bold text-background-50"
            >
              立即追更
            </Link>
          </nav>
          {seriesList.length > 0 && (
            <p className="mt-3 text-xs text-foreground-400">小贴士：搜索更多瓜，可在「追更大厅」页使用搜索框。</p>
          )}
        </div>
      )}
    </header>
  );
}